import { c as _c } from "react/compiler-runtime";
import { chakra } from '@chakra-ui/react';
import styled from '@emotion/styled';
import useOnScreen from '@src/hooks/useOnScreen';
import { useEffect, useMemo, useRef, useState } from 'react';

// Create a styled component for the fading text
export const FadeText = styled.span<{
  isFadedIn: boolean;
  delay?: string;
}>`
  opacity: ${props => props.isFadedIn ? 1 : 0};
  transition: opacity ${props => props.delay || '1s'} ease-in-out;
`;
export const useDelayedText = (finalText, t0, t1, t2) => {
  const $ = _c(13);
  const delayInMs = t0 === undefined ? 6 : t0;
  const trigger = t1 === undefined ? true : t1;
  const showInitial = t2 === undefined ? false : t2;
  const [shownText, setShownText] = useState(showInitial ? finalText : "");
  const [textToShow, setTextToShow] = useState(finalText);
  const charIndex = useRef(0);
  const wordIndex = useRef(0);
  let t3;
  let t4;
  if ($[0] !== finalText || $[1] !== showInitial) {
    t3 = () => {
      setTextToShow(prevTextToShow => {
        const newSplitType = (finalText || "").length > 250 ? "word" : "letter";
        const prevSplitType = (prevTextToShow || "").length > 250 ? "word" : "letter";
        if (finalText && prevTextToShow && finalText.startsWith(prevTextToShow) && newSplitType === prevSplitType) {
          return finalText;
        }
        charIndex.current = (showInitial ? finalText : "").length;
        wordIndex.current = (showInitial ? (finalText || "").split(/([ \t\n\r]+)/) : []).length;
        setShownText(showInitial ? finalText : "");
        return finalText || "";
      });
    };
    t4 = [finalText, showInitial];
    $[0] = finalText;
    $[1] = showInitial;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] !== delayInMs || $[5] !== shownText || $[6] !== textToShow || $[7] !== trigger) {
    t5 = () => {
      if (!textToShow || !trigger || shownText === textToShow) {
        return;
      }
      const splitType = (textToShow || "").length > 250 ? "word" : "letter";
      const interval = setInterval(() => {
        if (splitType === "word") {
          const words = (textToShow || "").split(/([ \t\n\r]+)/);
          if (wordIndex.current >= words.length) {
            setShownText(textToShow);
            clearInterval(interval);
            return;
          }
          wordIndex.current = wordIndex.current + 1;
          const newText = words.slice(0, wordIndex.current).join("");
          setShownText(newText);
        } else {
          if (charIndex.current >= textToShow.length) {
            setShownText(textToShow);
            clearInterval(interval);
            return;
          }
          charIndex.current = charIndex.current + 1;
          const newText_0 = textToShow.substring(0, charIndex.current);
          setShownText(newText_0);
        }
      }, delayInMs || 6);
      return () => clearInterval(interval);
    };
    $[4] = delayInMs;
    $[5] = shownText;
    $[6] = textToShow;
    $[7] = trigger;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== delayInMs || $[10] !== textToShow || $[11] !== trigger) {
    t6 = [textToShow, delayInMs, trigger];
    $[9] = delayInMs;
    $[10] = textToShow;
    $[11] = trigger;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  useEffect(t5, t6);
  return shownText;
};
interface TypingTextProps {
  children: string;
  delay?: number;
  fade?: boolean; // New prop to toggle fade effect
  repeat?: boolean;
  showInitial?: boolean;
}
export const TypingText = t0 => {
  const $ = _c(15);
  const {
    children,
    delay,
    repeat,
    showInitial,
    fade: t1
  } = t0;
  const fade = t1 === undefined ? true : t1;
  const finalText = children;
  const visibleRef = useRef(null);
  const isVisible = useOnScreen(visibleRef);
  const shownText = useDelayedText(finalText, delay, repeat === false || isVisible, showInitial);
  if (fade) {
    const splitType = (finalText || "").length > 250 ? "word" : "letter";
    let T0;
    let t2;
    let t3;
    if ($[0] !== finalText || $[1] !== shownText || $[2] !== splitType) {
      let parts;
      let shownParts = [];
      if (splitType === "word") {
        parts = (finalText || "").split(/([ \t\n\r]+)/);
        let t4;
        if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = /([ \t\n\r]+)/;
          $[6] = t4;
        } else {
          t4 = $[6];
        }
        shownParts = (shownText || "").split(t4);
      } else {
        parts = (finalText || "").split("");
        shownParts = (shownText || "").split("");
      }
      T0 = chakra.span;
      t2 = visibleRef;
      let t4;
      if ($[7] !== shownParts) {
        t4 = (part, index) => {
          const isVisible_0 = index < shownParts.length;
          return <FadeText key={index} isFadedIn={isVisible_0}>{part}</FadeText>;
        };
        $[7] = shownParts;
        $[8] = t4;
      } else {
        t4 = $[8];
      }
      t3 = parts.map(t4);
      $[0] = finalText;
      $[1] = shownText;
      $[2] = splitType;
      $[3] = T0;
      $[4] = t2;
      $[5] = t3;
    } else {
      T0 = $[3];
      t2 = $[4];
      t3 = $[5];
    }
    let t4;
    if ($[9] !== T0 || $[10] !== t2 || $[11] !== t3) {
      t4 = <T0 ref={t2}>{t3}</T0>;
      $[9] = T0;
      $[10] = t2;
      $[11] = t3;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return t4;
  }
  let t2;
  if ($[13] !== shownText) {
    t2 = <chakra.span ref={visibleRef}>{shownText}</chakra.span>;
    $[13] = shownText;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
};